import React, { useEffect, useRef, useState } from "react";
import ReactDom from "react-dom";
import { RemoveScroll } from "react-remove-scroll";
import { useDispatch, useSelector } from "react-redux";
import OtpInput from "react-otp-input";
import { Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  generateContactOtpThunk,
  validateContactOtpThunk,
  resetContactOtp,
} from "../../Redux/thunks/contactOtpThunks";

/**
 * Sequential OTP verification for contact-field changes.
 * Phone and email are verified one after another — step 2 only
 * starts after step 1 is successfully verified.
 *
 * steps: [{ channel: 'phone' | 'email', value: string }]
 * submitting: true while the update request is in flight after all OTPs
 */
const ContactOtpModal = ({
  open,
  steps = [],
  onVerified,
  onClose,
  submitting = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const contactOtp = useSelector((state) => state.contactOtp);
  const [stepIndex, setStepIndex] = useState(0);
  const [otp, setOtp] = useState("");
  const [resendTime, setResendTime] = useState(null);
  const [remainingTime, setRemainingTime] = useState(30);
  const [verifiedChannels, setVerifiedChannels] = useState([]);

  // Avoid re-sending OTP when React Strict Mode remounts effects
  const otpSentForStep = useRef(null);

  const currentStep = steps[stepIndex];
  const channel = currentStep?.channel;
  const channelState = channel ? contactOtp[channel] : null;
  const transactionId = channelState?.transactionId;
  const isLoading = channelState?.isLoading;
  const busy = isLoading || submitting;
  const totalSteps = steps.length;

  // Reset everything when the modal opens
  useEffect(() => {
    if (!open) {
      otpSentForStep.current = null;
      return;
    }
    setStepIndex(0);
    setOtp("");
    setVerifiedChannels([]);
    setResendTime(Date.now());
    setRemainingTime(30);
    otpSentForStep.current = null;
    dispatch(resetContactOtp());
  }, [open, dispatch]);

  // Send OTP only for the current step (never for the next until this one is verified)
  useEffect(() => {
    if (!open || submitting || !steps[stepIndex]) return;

    const step = steps[stepIndex];
    const stepKey = `${stepIndex}:${step.channel}:${step.value}`;
    if (otpSentForStep.current === stepKey) return;
    otpSentForStep.current = stepKey;

    setOtp("");
    setResendTime(Date.now());
    setRemainingTime(30);
    dispatch(generateContactOtpThunk(step.channel, step.value));
    console.log("transactionId", transactionId);
  }, [open, stepIndex, steps, dispatch, submitting]);

  useEffect(() => {
    if (!resendTime || submitting) return;
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - resendTime) / 1000);
      if (elapsed >= 30) {
        clearInterval(timer);
        setResendTime(null);
        setRemainingTime(30);
      } else {
        setRemainingTime(30 - elapsed);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [resendTime, submitting]);

  if (!open) return null;
  if (!currentStep && !submitting) return null;

  const handleVerify = async (event) => {
    event.preventDefault();
    if (otp.length !== 6 || submitting || !currentStep) return;
    console.log("transactionId", transactionId);
    if (!transactionId) {
      return;
    }

    const ok = await dispatch(
      validateContactOtpThunk(channel, currentStep.value, otp, transactionId)
    );

    if (!ok) return;

    const nextVerified = [...verifiedChannels, channel];
    setVerifiedChannels(nextVerified);

    // More steps remaining → go to the next channel (e.g. email after phone)
    if (stepIndex < totalSteps - 1) {
      setOtp("");
      setStepIndex((i) => i + 1);
      return;
    }

    // All steps verified → submit update
    onVerified();
  };

  const handleResend = () => {
    if (submitting || !currentStep) return;
    // Allow a fresh send so a new transactionId can be stored
    otpSentForStep.current = null;
    setResendTime(Date.now());
    setRemainingTime(30);
    dispatch(generateContactOtpThunk(channel, currentStep.value));
  };

  const maskedTarget =
    channel === "phone"
      ? currentStep?.value?.replace(/(\d{3})\d+(\d{2})/, "$1****$2")
      : currentStep?.value?.replace(/(.{2}).+(@.+)/, "$1****$2");

  const titleKey =
    channel === "phone" ? "contact-otp-phone-title" : "contact-otp-email-title";
  const descKey =
    channel === "phone"
      ? "contact-otp-phone-description"
      : "contact-otp-email-description";

  return ReactDom.createPortal(
    <div className="fixed top-0 left-0 right-0 h-screen bg-black/50 z-[100]">
      <RemoveScroll className="flex justify-center h-full flex-col p-6">
        <div
          className="h-full w-full z-10 absolute"
          onClick={busy ? undefined : onClose}
        />
        <div className="bg-white z-50 w-full max-w-md mx-auto rounded-xl shadow-lg overflow-hidden">
          {submitting ? (
            <div className="p-10 flex flex-col items-center justify-center gap-4 text-MetallicBlue">
              <Loader className="animate-spin w-10 h-10 text-MetallicBlue" />
              <p className="text-lg font-semibold text-center">
                {t("update-button-submitting")}
              </p>
              <p className="text-sm text-center text-gray-500">
                {t("contact-otp-submitting-description")}
              </p>
            </div>
          ) : (
            <form onSubmit={handleVerify}>
              <div className="bg-MetallicBlue p-6 text-white">
                <p className="text-sm opacity-80">
                  {t("contact-otp-step", {
                    current: stepIndex + 1,
                    total: totalSteps,
                  })}
                </p>

                {/* Visual step progress when both phone and email need verification */}
                {totalSteps > 1 && (
                  <div className="flex items-center gap-2 mt-3 text-sm">
                    {steps.map((step, index) => {
                      const done = verifiedChannels.includes(step.channel);
                      const active = index === stepIndex;
                      const label =
                        step.channel === "phone"
                          ? t("contact-otp-channel-phone")
                          : t("contact-otp-channel-email");
                      return (
                        <React.Fragment key={step.channel}>
                          {index > 0 && (
                            <span className="opacity-50">→</span>
                          )}
                          <span
                            className={`px-2 py-0.5 rounded ${
                              done
                                ? "bg-green-500/90"
                                : active
                                ? "bg-white text-MetallicBlue font-semibold"
                                : "bg-white/20 opacity-70"
                            }`}
                          >
                            {done ? `✓ ${label}` : label}
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

                <p className="text-xl font-semibold mt-3">{t(titleKey)}</p>
                <p className="text-sm mt-2 opacity-90">
                  {t(descKey, { target: maskedTarget })}
                </p>
              </div>

              <div className="p-6 flex flex-col gap-4 text-MetallicBlue">
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  shouldAutoFocus
                  inputType="tel"
                  inputStyle={{
                    borderRadius: "0.5rem",
                    width: "2.5rem",
                    height: "2.5rem",
                    textAlign: "center",
                    color: "black",
                    border: "2px solid #184C61",
                  }}
                  renderSeparator={<span className="px-1" />}
                  renderInput={(props) => <input {...props} />}
                />

                {channelState?.error && (
                  <p className="text-red-500 text-sm text-center">
                    {channelState.error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={busy || otp.length !== 6 || !transactionId}
                  className="px-8 py-2 rounded-lg bg-[#FFB400] border border-MetallicBlue disabled:opacity-50"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader className="animate-spin w-4 h-4" />
                      {t("otp-button-verifying")}
                    </span>
                  ) : stepIndex < totalSteps - 1 ? (
                    t("contact-otp-next-step")
                  ) : (
                    t("otp-button-verify")
                  )}
                </button>

                <div className="flex items-center justify-center gap-1 text-sm">
                  <span>{t("otp-resend-text")}</span>
                  <button
                    type="button"
                    className="text-nidblue disabled:opacity-50"
                    onClick={handleResend}
                    disabled={Boolean(resendTime) || busy}
                  >
                    {resendTime
                      ? `${t("otp-resend-text2")} ${remainingTime} ${t(
                          "otp-resend-text3"
                        )}`
                      : t("otp-resend-text1")}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="text-sm text-gray-500 hover:underline"
                >
                  {t("Cancel")}
                </button>
              </div>
            </form>
          )}
        </div>
      </RemoveScroll>
    </div>,
    document.getElementById("portal")
  );
};

export default ContactOtpModal;
